/**
 * The Stocks section's route bindings — where the API is wired to the four screens.
 *
 * The screens take their data as props and know nothing about where it came from, which is what
 * made this swap one file rather than four rewrites. The previous version of this comment promised
 * exactly that, and it held: each wrapper became a `useQuery` with the loading and error states the
 * rest of the app already models, `fixture.ts` was deleted, and `FixtureNote` went with it.
 *
 * 🔴 **`FixtureNote` was removed by deletion, not by a flag**, which is what its own docstring
 * demanded: *"a boolean would let the marker be switched off before the endpoint was real"*. The
 * endpoints are real, the fixture file is gone, and there is no way to render a fixture figure now
 * without adding one back on purpose.
 *
 * ⚠ **What the API does not carry, `adapt.ts` reports as absent rather than as zero** — the dossier
 * spend block and the pre-registration window. Phases 8–9 are deferred, so there are no dossiers and
 * no spend; a row of zeros would read as a measurement.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';
import { Link, useParams } from 'react-router-dom';

import type { Screen } from './model';
import { api } from '../../api/client';
import { EmptyState } from '../../design/components/messages/EmptyState.jsx';
import { ErrorPanel } from '../../design/components/messages/ErrorPanel.jsx';
import { Skeleton } from '../../design/components/messages/Skeleton.jsx';
import { adaptCompany, adaptCoverage, adaptScorecard, adaptScreenerRun } from './adapt';
import { CompanyScreen } from './CompanyScreen';
import { CoverageScreen } from './CoverageScreen';
import { ScorecardScreen } from './ScorecardScreen';
import { NewsScreen, type NewsItem } from './NewsScreen';
import type { NewsDeskCounts } from './NewsDesk';
import { TrumpWatchScreen, type TrumpRate, type TrumpStatement } from './TrumpWatchScreen';
import type { TrumpWatchCounts, TrumpWatchRow } from './TrumpBoard';
import { ScreenerScreen } from './ScreenerScreen';

/** Loading and error chrome, identical across the four routes so none of them invents its own. */
function Pending(): JSX.Element {
  return (
    <div className="jmt-screen">
      <Skeleton count={8} />
    </div>
  );
}

function Failed({
  what,
  error,
  retry,
}: {
  what: string;
  error: unknown;
  retry: () => void;
}): JSX.Element {
  return (
    <div className="jmt-screen">
      <ErrorPanel
        message={`Could not load ${what}.`}
        detail={String((error as Error)?.message ?? '')}
        onRetry={retry}
      />
    </div>
  );
}

/**
 * How often the News page re-asks.
 *
 * ⚠ The poller refreshes prices every minute, so 30s is the honest ceiling on freshness: asking
 * faster cannot produce a newer number, because the number is a column the poller wrote. Every row
 * also shows when ITS price was read, so a stalled reading is visible rather than implied.
 */
const NEWS_REFETCH_MS = 30_000;

/** The window both the feed and its freshness strip describe. One constant, deliberately. */
const NEWS_WINDOW_HOURS = 48;

export function NewsRoute(): JSX.Element {
  // ⚠ Recency is the default. Impact is a toggle rather than the order, because the feed's job is
  // to say what just happened — see NewsScreen's header.
  const [sort, setSort] = useState<'recent' | 'impact' | 'expected'>('recent');
  const feed = useQuery({
    queryKey: ['stocks', 'news', sort],
    queryFn: () => api.stocks.getNews({ hours: NEWS_WINDOW_HOURS, sort }),
    // ⚠ `refetchIntervalInBackground` is deliberately left off: a tab nobody is looking at should
    // not keep polling.
    refetchInterval: NEWS_REFETCH_MS,
  });
  // 🔴 **The SAME `hours` as the feed, from one constant.** A strip describing 48 hours above a
  // list showing 24 is a header that disagrees with its own table, and the two would drift the
  // first time either window was tuned.
  const desk = useQuery({
    queryKey: ['stocks', 'news', 'desk'],
    queryFn: () => api.stocks.getNewsDesk({ hours: NEWS_WINDOW_HOURS }),
    refetchInterval: NEWS_REFETCH_MS,
  });

  if (feed.isLoading) return <Pending />;
  if (feed.isError) {
    return <Failed what="the news feed" error={feed.error} retry={() => void feed.refetch()} />;
  }
  return (
    <NewsScreen
      items={(feed.data ?? []) as NewsItem[]}
      sort={sort}
      onSort={setSort}
      // ⚠ Null rather than blocking the page on it: the strip is context, the stories are the page.
      desk={(desk.data ?? null) as NewsDeskCounts | null}
    />
  );
}

/**
 * How often Trump Watch re-asks.
 *
 * ⚠ **Slower than News on purpose.** The collector ticks every few minutes and prices only the last
 * 48 hours, so a 30-second refetch could not produce a newer number — it would just be traffic. Two
 * minutes matches the fastest thing upstream that can actually change.
 */
const TRUMP_REFETCH_MS = 120_000;

/**
 * `/trump`.
 *
 * ⚠ **Two queries, and the rates one is not gated on the feed.** The class strip is how the reader
 * navigates, so it has to be there even when the current filter returns nothing — otherwise
 * choosing a class with no rows in the window strands them on a page with no way back.
 *
 * ⚠ The filter is a **server-side** parameter rather than a client-side array filter: the window is
 * a week and the limit is 100, so filtering locally would silently show "the trade statements among
 * the newest 100" rather than "the newest 100 trade statements".
 */
export function TrumpWatchRoute(): JSX.Element {
  const [filter, setFilter] = useState<string | null>(null);
  const feed = useQuery({
    queryKey: ['stocks', 'trump', filter],
    queryFn: () => api.stocks.getTrump({ hours: 168, policyClass: filter, limit: 100 }),
    refetchInterval: TRUMP_REFETCH_MS,
  });
  const rates = useQuery({
    queryKey: ['stocks', 'trump', 'rates'],
    queryFn: () => api.stocks.getTrumpRates({ days: 90 }),
  });
  // ⚠ **Not gated on `filter`, deliberately.** The class filter belongs to the statement log; the
  // board is the whole week's companies and narrowing it with the log's filter would make the four
  // header tiles disagree with their own labels ("names touched, 7 days" counting only trade).
  const board = useQuery({
    queryKey: ['stocks', 'trump', 'watch'],
    queryFn: () => api.stocks.getTrumpWatch({ days: 7, limit: 200 }),
    refetchInterval: TRUMP_REFETCH_MS,
  });

  if (feed.isLoading) return <Pending />;
  if (feed.isError) {
    return (
      <Failed what="the Trump Watch feed" error={feed.error} retry={() => void feed.refetch()} />
    );
  }
  return (
    <TrumpWatchScreen
      items={(feed.data ?? []) as TrumpStatement[]}
      // ⚠ An empty array when the rates query is still in flight or failed, rather than blocking
      // the whole screen on it. The strip is navigation; the statements are the page.
      rates={(rates.data ?? []) as TrumpRate[]}
      filter={filter}
      onFilter={setFilter}
      // ⚠ Empty rather than blocking the page: the board is the lead, but a statement log that
      // renders is better than a screen that renders nothing because one of three queries is slow.
      companies={(board.data?.companies ?? []) as TrumpWatchRow[]}
      counts={(board.data?.counts ?? null) as TrumpWatchCounts | null}
    />
  );
}

export function ScreenerRoute(): JSX.Element {
  // 🔴 The screen is part of the query key, not just the request. Two screens share this route
  // and their rows have different shapes — a cached value payload rendered under the growth tab
  // would be a page with no market caps and no explanation of why.
  //
  // ⚠ Keyed on the SCREEN, not the ruleset version. It was `'g1'` here and `'r1'` in the
  // request, so when RULESET_VERSION moved to `r2` the value tab asked for a version the
  // job had stopped writing and silently showed a frozen run instead of the newest one.
  const [ruleset, setRuleset] = useState<Screen>('growth');
  const run = useQuery({
    queryKey: ['stocks', 'screen', ruleset],
    queryFn: () => api.stocks.getScreen({ screen: ruleset }),
  });

  if (run.isLoading) return <Pending />;
  if (run.isError) {
    return <Failed what="the screener" error={run.error} retry={() => void run.refetch()} />;
  }
  if (!run.data) {
    // ⚠ A run that has never happened is not an error. Said as a gap in the record rather than as a
    // failure, because the first morning after a deploy is exactly this state.
    return (
      <div className="jmt-screen">
        <div className="jmt-note">
          <EmptyState>
            No {ruleset} run yet. The daily job writes one each
            weekday morning; until it has run there is nothing to show, which is different from
            nothing qualifying.
          </EmptyState>
        </div>
      </div>
    );
  }
  return <ScreenerScreen run={adaptScreenerRun(run.data)} ruleset={ruleset} onRuleset={setRuleset} />;
}

export function ScorecardRoute(): JSX.Element {
  const card = useQuery({
    queryKey: ['stocks', 'scorecard'],
    queryFn: () => api.stocks.getScorecard(),
  });

  if (card.isLoading) return <Pending />;
  if (card.isError) {
    return <Failed what="the scorecard" error={card.error} retry={() => void card.refetch()} />;
  }
  if (!card.data) return <Pending />;
  return <ScorecardScreen card={adaptScorecard(card.data)} />;
}

export function CoverageRoute(): JSX.Element {
  const coverage = useQuery({
    queryKey: ['stocks', 'coverage'],
    queryFn: () => api.stocks.getCoverage(),
  });

  if (coverage.isLoading) return <Pending />;
  if (coverage.isError) {
    return <Failed what="coverage" error={coverage.error} retry={() => void coverage.refetch()} />;
  }
  if (!coverage.data) return <Pending />;
  return <CoverageScreen coverage={adaptCoverage(coverage.data)} />;
}

/**
 * `/screener/:isin`.
 *
 * ⚠ **Keyed by ISIN, not by ticker.** A ticker belongs to a listing — `KLAC` on NASDAQ and
 * `KLA GY` on XETR are one company — and tickers are reassigned after a delisting. A bookmarked
 * dossier has to survive both.
 *
 * The path nests under `/screener` so `screenFor()` keys the sidebar off the first segment and the
 * Screener row stays highlighted while a dossier is open, with no extra state in the shell. It also
 * means the URL says where the operator came from, which is what the in-page back control claims.
 */
export function CompanyRoute(): JSX.Element {
  const { isin } = useParams<{ isin: string }>();
  const company = useQuery({
    queryKey: ['stocks', 'company', isin],
    queryFn: () => api.stocks.getCompany({ isin: isin ?? '' }),
    enabled: Boolean(isin),
  });

  if (!isin) {
    return (
      <div className="jmt-screen">
        <div className="jmt-note">
          <EmptyState>No company in the address.</EmptyState>
        </div>
        <p className="jmt-note-text">
          <Link to="/screener">Back to the screener</Link>
        </p>
      </div>
    );
  }
  if (company.isLoading) return <Pending />;
  if (company.isError) {
    // 🔴 A 404 means the ISIN is not in the universe, which is a different statement from "the
    // request failed" — and the operator arrived here by following a link, so it is worth saying
    // which. The screener would not have linked to a company it does not hold.
    const status = (company.error as { status?: number })?.status;
    if (status === 404) {
      return (
        <div className="jmt-screen">
          <div className="jmt-note">
            <EmptyState>
              No company {isin} in the universe. Either Trading 212 does not offer it, or it left the
              universe since the link was made — the Coverage screen says which.
            </EmptyState>
          </div>
          <p className="jmt-note-text">
            <Link to="/screener">Back to the screener</Link>
          </p>
        </div>
      );
    }
    return (
      <Failed
        what={`the dossier for ${isin}`}
        error={company.error}
        retry={() => void company.refetch()}
      />
    );
  }
  if (!company.data) return <Pending />;
  return <CompanyScreen company={adaptCompany(company.data)} />;
}
